/**
 * Account deletion API (native iOS – App Store account deletion requirement)
 */

import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type { ApiResponse } from '@/api/types';

const LOG_PREFIX = '[AccountDeletion]';

function logApi(
  phase: 'request' | 'response' | 'error',
  details: Record<string, unknown>
): void {
  // eslint-disable-next-line no-console
  console.log(LOG_PREFIX, phase, JSON.stringify(details, null, 2));
}

function logAxiosError(method: string, url: string, err: unknown): void {
  const ax = err as AxiosError<unknown> | undefined;
  const status = ax?.response?.status;
  const data = ax?.response?.data;
  logApi('error', {
    method,
    url,
    message: ax?.message ?? String(err),
    httpStatus: status ?? null,
    responseBody: data ?? null,
  });
}

export interface AccountDeletionStatusData {
  is_ios_client: boolean;
  has_deletion_request: boolean;
  can_submit: boolean;
  submitted_at: string | null;
  reason_code: number | null;
  reason_label: string | null;
}

export interface AccountDeletionReason {
  id: number;
  label: string;
}

export interface AccountDeletionReasonsData {
  heading: string;
  reasons: AccountDeletionReason[];
  is_ios_client: boolean;
  /** Returned by GET account-deletion/reasons/ — use for POST account-deletion body */
  artist_id?: number;
  artist_user_id?: number;
}

function parsePositiveId(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'string' ? Number(value.trim()) : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

/** Walk response JSON for `artist_id` (nested wrappers supported). */
function findArtistIdInResponse(node: unknown, depth = 0): number | null {
  if (depth > 10 || node == null || typeof node !== 'object') return null;
  const o = node as Record<string, unknown>;
  const direct = parsePositiveId(o.artist_id);
  if (direct != null) return direct;
  for (const v of Object.values(o)) {
    const found = findArtistIdInResponse(v, depth + 1);
    if (found != null) return found;
  }
  return null;
}

function isApiSuccessPayload(node: unknown, depth = 0): boolean {
  if (depth > 8 || node == null || typeof node !== 'object') return false;
  const o = node as Record<string, unknown>;
  if (o.success === true) return true;
  if (o.data != null && typeof o.data === 'object') {
    return isApiSuccessPayload(o.data, depth + 1);
  }
  return false;
}

/**
 * Backend may return standard `{ success, data }` or an extra wrapper `{ data: { success, data } }`.
 * Find the object that has `reasons` + `heading` and optional `artist_id`.
 */
function findAccountDeletionReasonsPayload(node: unknown, depth = 0): AccountDeletionReasonsData | null {
  if (depth > 8 || node == null || typeof node !== 'object') return null;
  const o = node as Record<string, unknown>;
  if (Array.isArray(o.reasons) && typeof o.heading === 'string') {
    const artistId = parsePositiveId(o.artist_id);
    const artistUserId = parsePositiveId(o.artist_user_id);
    return {
      heading: o.heading,
      reasons: o.reasons as AccountDeletionReason[],
      is_ios_client: Boolean(o.is_ios_client),
      ...(artistId != null ? { artist_id: artistId } : {}),
      ...(artistUserId != null ? { artist_user_id: artistUserId } : {}),
    };
  }
  for (const v of Object.values(o)) {
    const found = findAccountDeletionReasonsPayload(v, depth + 1);
    if (found) return found;
  }
  return null;
}

export class AccountDeletionService {
  static async getStatus(): Promise<AccountDeletionStatusData> {
    const url = API_ENDPOINTS.AUTH.ACCOUNT_DELETION_STATUS;
    logApi('request', { method: 'GET', url, body: null });

    try {
      const response = await apiClient.get<ApiResponse<AccountDeletionStatusData>>(url);
      logApi('response', {
        method: 'GET',
        url,
        httpStatus: response.status,
        responseHeaders: response.headers ?? {},
        data: response.data,
      });

      const payload = response.data;
      if (!payload?.success || !payload.data) {
        logApi('error', {
          method: 'GET',
          url,
          message: 'Unexpected payload: success false or missing data',
          parsedPayload: payload ?? null,
        });
        throw new Error('Failed to load account deletion status');
      }
      return payload.data;
    } catch (err) {
      if (isAxiosError(err)) {
        logAxiosError('GET', url, err);
      } else {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'error', err);
      }
      throw err;
    }
  }

  static async getReasons(): Promise<AccountDeletionReasonsData> {
    const url = API_ENDPOINTS.AUTH.ACCOUNT_DELETION_REASONS;
    logApi('request', { method: 'GET', url, body: null });

    try {
      const response = await apiClient.get<ApiResponse<AccountDeletionReasonsData>>(url);
      logApi('response', {
        method: 'GET',
        url,
        httpStatus: response.status,
        responseHeaders: response.headers ?? {},
        data: response.data,
      });

      const extracted = findAccountDeletionReasonsPayload(response.data);
      if (!extracted) {
        logApi('error', {
          method: 'GET',
          url,
          message: 'Could not parse reasons payload (missing heading/reasons)',
          raw: response.data,
        });
        throw new Error('Failed to load deletion reasons');
      }
      return extracted;
    } catch (err) {
      if (isAxiosError(err)) {
        logAxiosError('GET', url, err);
      } else {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'error', err);
      }
      throw err;
    }
  }

  static async submitDeletion(artistId: number, reasonCode: number): Promise<void> {
    const url = API_ENDPOINTS.AUTH.ACCOUNT_DELETION_SUBMIT;
    const body = { artist_id: artistId, reason_code: reasonCode };
    logApi('request', { method: 'POST', url, body });

    try {
      const response = await apiClient.post<ApiResponse<unknown>>(url, body);
      logApi('response', {
        method: 'POST',
        url,
        httpStatus: response.status,
        responseHeaders: response.headers ?? {},
        data: response.data,
      });

      const payload = response.data;
      if (!payload?.success) {
        logApi('error', {
          method: 'POST',
          url,
          message: 'Unexpected payload: success false',
          parsedPayload: payload ?? null,
        });
        throw new Error('Failed to submit account deletion request');
      }
    } catch (err) {
      if (isAxiosError(err)) {
        logAxiosError('POST', url, err);
      } else {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'error', err);
      }
      throw err;
    }
  }

  /**
   * GET account-deletion/reactivate/ — returns artist_id for the pending deletion request.
   */
  static async getReactivateArtistId(): Promise<number> {
    const url = API_ENDPOINTS.AUTH.ACCOUNT_DELETION_REACTIVATE;
    logApi('request', { method: 'GET', url, body: null });

    try {
      const response = await apiClient.get<ApiResponse<unknown>>(url);
      logApi('response', {
        method: 'GET',
        url,
        httpStatus: response.status,
        responseHeaders: response.headers ?? {},
        data: response.data,
      });

      const root = response.data;
      const artistId =
        findArtistIdInResponse(root) ??
        (root &&
        typeof root === 'object' &&
        'data' in (root as Record<string, unknown>)
          ? findArtistIdInResponse((root as ApiResponse<unknown>).data)
          : null);

      if (artistId == null) {
        logApi('error', {
          method: 'GET',
          url,
          message: 'artist_id missing from GET reactivate response',
          raw: root ?? null,
        });
        throw new Error('artist_id missing from account-deletion/reactivate/');
      }

      return artistId;
    } catch (err) {
      if (isAxiosError(err)) {
        logAxiosError('GET', url, err);
      } else {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'error', err);
      }
      throw err;
    }
  }

  static async postReactivateAccount(artistId: number): Promise<void> {
    const url = API_ENDPOINTS.AUTH.ACCOUNT_DELETION_REACTIVATE;
    const body = { artist_id: artistId };
    logApi('request', { method: 'POST', url, body });

    try {
      const response = await apiClient.post<ApiResponse<unknown>>(url, body);
      logApi('response', {
        method: 'POST',
        url,
        httpStatus: response.status,
        responseHeaders: response.headers ?? {},
        data: response.data,
      });

      const payload = response.data;
      if (!isApiSuccessPayload(payload)) {
        logApi('error', {
          method: 'POST',
          url,
          message: 'Unexpected payload: success false or missing',
          parsedPayload: payload ?? null,
        });
        throw new Error('Failed to reactivate account');
      }
    } catch (err) {
      if (isAxiosError(err)) {
        logAxiosError('POST', url, err);
      } else {
        // eslint-disable-next-line no-console
        console.log(LOG_PREFIX, 'error', err);
      }
      throw err;
    }
  }

  /** GET reactivate (artist_id) → POST reactivate { artist_id }. */
  static async reactivateAccount(): Promise<void> {
    const artistId = await AccountDeletionService.getReactivateArtistId();
    await AccountDeletionService.postReactivateAccount(artistId);
  }
}
